import * as d3 from 'd3';
import {BaseType} from 'd3-selection';
import {EmployeeDataType} from '../../../../data-management/data-sources/sources/employees-data-source';
import {Gui} from '../../../gui';
import {DetailComponent} from '../detail-component';
import {EmployeeLinkData, EmployeeNodeData, EmployeeRelationshipType} from './user-detail-contracts';
import {GeneralConstants} from '../../../../util/app-constants';

export class UserDetailComponent extends DetailComponent<EmployeeDataType, EmployeeNodeData, EmployeeLinkData> {
    private employees: EmployeeDataType[];

    constructor(gui: Gui, jQueryElement: JQuery<HTMLElement>) {
        super(gui, jQueryElement);
        this.employees = this.gui.app.dataSourceLoader.employeesDataSource.data;
    }

    public setElement(elementId: any) {
        this.selectedElement = this.employees
            .filter(employee => employee._internal_datasource_id_ === elementId)
            .shift();
        this.setSelectedInfo();
    }

    protected initLinks() {
        const skilldLink = this.jQueryElement.find('.context-detail-component__link--skilld');
        skilldLink.show();
        skilldLink.click(() => {
            this.openUrlInNewTab(`${GeneralConstants.skilldProfileUrl}${this.selectedElement.Email}`);
        });
        const capacityPlanLink = this.jQueryElement.find('.context-detail-component__link--capacity-plan');
        capacityPlanLink.show();
        capacityPlanLink.click(() => {
            this.openUrlInNewTab(`${GeneralConstants.capacityPlanSearchUrl}${this.selectedElement.Name}`);
        });
    }

    protected cleanLinks() {
        const skilldLink = this.jQueryElement.find('.context-detail-component__link--skilld');
        skilldLink.hide();
        skilldLink.off('click');
        const capacityPlanLink = this.jQueryElement.find('.context-detail-component__link--capacity-plan');
        capacityPlanLink.hide();
        capacityPlanLink.off('click');
    }

    protected buildSimulationData() {
        this.links = [];
        this.nodes = [];

        this.addRelation(this.selectedElement, EmployeeRelationshipType.None);

        if (
            this.selectedElement.EngagementManagerId &&
            this.selectedElement.EngagementManagerId !== this.selectedElement.Id &&
            this.isEmployed(this.selectedElement.EngagementManagerId)
        ) {
            const selectedEmployeeEm = this.gui.app.dataSourceLoader.employeesDataSource.employeeIdToEmployee[
                this.selectedElement.EngagementManagerId
                ];
            this.addRelation(selectedEmployeeEm, EmployeeRelationshipType.EM);
        }
        const emCoachies =
            this.gui.app.dataSourceLoader.employeesDataSource.engagementManagerIdToCoachees[this.selectedElement.Id] ||
            [];
        emCoachies.forEach(coachee => this.addRelation(coachee, EmployeeRelationshipType.EMCoachee));

        if (
            this.selectedElement.LTCoachId &&
            this.selectedElement.LTCoachId !== this.selectedElement.Id &&
            this.isEmployed(this.selectedElement.LTCoachId)
        ) {
            const selectedEmployeeLTCoach = this.gui.app.dataSourceLoader.employeesDataSource.employeeIdToEmployee[
                this.selectedElement.LTCoachId
                ];
            this.addRelation(selectedEmployeeLTCoach, EmployeeRelationshipType.LTCoach);
        }
        const ltCoachies =
            this.gui.app.dataSourceLoader.employeesDataSource.ltCoachesToCoachees[this.selectedElement.Id] || [];
        ltCoachies.forEach(coachee => this.addRelation(coachee, EmployeeRelationshipType.LTCoachee));

        if (
            this.selectedElement.FirestaerterId &&
            this.selectedElement.FirestaerterId !== this.selectedElement.Id &&
            this.isEmployed(this.selectedElement.FirestaerterId)
        ) {
            const selectedEmployeeFirestaerter = this.gui.app.dataSourceLoader.employeesDataSource.employeeIdToEmployee[
                this.selectedElement.FirestaerterId
                ];
            this.addRelation(selectedEmployeeFirestaerter, EmployeeRelationshipType.Firestaerter);
        }
        const firestaerterCoachies =
            this.gui.app.dataSourceLoader.employeesDataSource.firestaerterIdToCoachees[this.selectedElement.Id] || [];
        firestaerterCoachies.forEach(coachee => this.addRelation(coachee, EmployeeRelationshipType.FirestaerterCoachee));
    }

    private addRelation(employee: EmployeeDataType, relationshipType: EmployeeRelationshipType) {
        const existingNode = this.nodes.filter(node => node.element.Id === employee.Id).shift();
        if (existingNode) {
            existingNode.relationshipType |= relationshipType;
        } else {
            this.nodes.push(new EmployeeNodeData(employee, relationshipType));
        }

        if (relationshipType !== EmployeeRelationshipType.None) {
            const existingLink = this.links
                .filter(link => link.target === employee.Id && link.source === this.selectedElement.Id)
                .shift();


            if (
                (existingLink &&
                    relationshipType !== EmployeeRelationshipType.EM &&
                    relationshipType !== EmployeeRelationshipType.LTCoach)
                || (
                    existingLink &&
                    relationshipType !== EmployeeRelationshipType.EM &&
                    relationshipType !== EmployeeRelationshipType.Firestaerter
                ) || (
                    existingLink &&
                    relationshipType !== EmployeeRelationshipType.Firestaerter &&
                    relationshipType !== EmployeeRelationshipType.LTCoach
                )) {
                // Make separate link for EM / LT & EM / Firestaerter & LTC / Firestaerter Coach so we have a separate label
                existingLink.relationshipType |= relationshipType;
            } else {
                this.links.push(new EmployeeLinkData(this.selectedElement.Id, employee.Id, relationshipType));
            }
        }
    }

    private isEmployed(id: string): boolean {
        return this.gui.app.dataSourceLoader.employeesDataSource.employeeIdToEmployee[id] !== undefined;
    }

    /**
     * Simulation stuff
     */

    protected forceXNode(node: EmployeeNodeData) {
        if (node.isCoach()) {
            return this.svgCanvasWidth - this.svgCanvasWidth / 5.5;
        }
        if (node.isCoachee()) {
            return this.svgCanvasWidth / 5.5;
        }
        return this.centerX;
    }

    protected forceYNode(node: EmployeeNodeData) {

        if (node.hasEMRelation() && !node.hasLTCoachRelation()) {
            return this.svgCanvasHeight - this.svgCanvasHeight / 4;
        }
        if ((!node.hasEMRelation() && node.hasLTCoachRelation()) || (node.hasEMRelation() && node.hasLTCoachRelation())) {
            return this.svgCanvasHeight / 4;
        }

        return this.centerY;
    }

    protected linkClass(link: EmployeeLinkData) {
        let className = 'relation-link';
        if (link.hasEMRelation()) {
            className += ' relation-link--em';
        }
        if (link.hasLTCoachRelation()) {
            className += ' relation-link--lt-coach';
        }
        if (link.hasFirestaerterCoachRelation()) {
            className += ' relation-link--firestaerter';
        }
        return className;
    }

    protected linkLabelClass(link: EmployeeLinkData) {
        let className = 'relation-link-label';
        if (link.hasEMRelation()) {
            className += ' relation-link-label--em';
        }
        if (link.hasLTCoachRelation()) {
            className += ' relation-link-label--lt-coach';
        }
        if (link.hasFirestaerterCoachRelation()) {
            className += ' relation-link-label--firestaerter';
        }
        return className;
    }

    protected linkLabelText(link: EmployeeLinkData) {
        switch (link.relationshipType) {
            case EmployeeRelationshipType.EM:
                return 'EM';
            case EmployeeRelationshipType.LTCoach:
                return 'LT Coach';
            case EmployeeRelationshipType.Firestaerter:
                return 'Firestaerter';
            case EmployeeRelationshipType.EM | EmployeeRelationshipType.LTCoach:
                return 'EM / LT Coach';
            case EmployeeRelationshipType.EM | EmployeeRelationshipType.Firestaerter:
                return 'EM / Firestaerter';
            case EmployeeRelationshipType.LTCoach | EmployeeRelationshipType.Firestaerter:
                return 'LTC / Firestaerter';
            case EmployeeRelationshipType.EMCoachee:
                return 'EM Coachee';
            case EmployeeRelationshipType.LTCoachee:
                return 'LT Coachee';
            case EmployeeRelationshipType.FirestaerterCoachee:
                return 'Firestaerter Coachee';
            case EmployeeRelationshipType.EMCoachee | EmployeeRelationshipType.LTCoachee :
            case EmployeeRelationshipType.EMCoachee | EmployeeRelationshipType.FirestaerterCoachee:
            case EmployeeRelationshipType.LTCoachee | EmployeeRelationshipType.FirestaerterCoachee:
                return 'Coachee';
            default:
                return '';
        }
    }

    protected linkLabelX(link: EmployeeLinkData) {
        const source = link.source as EmployeeNodeData;
        const target = link.target as EmployeeNodeData;
        return source.x + (target.x - source.x) / (link.isCoachee()
            ? 1.3
            : link.relationshipType === EmployeeRelationshipType.Firestaerter ? 2 : 1.6);
    }

    protected linkLabelY(link: EmployeeLinkData) {
        const source = link.source as EmployeeNodeData;
        const target = link.target as EmployeeNodeData;
        let correction = -2;
        if (
            (source.y - 5 > target.y && source.x < target.x) ||
            (source.y + 5 < target.y && source.x > target.x) ||
            link.relationshipType === EmployeeRelationshipType.LTCoach ||
            link.relationshipType === EmployeeRelationshipType.Firestaerter
        ) {
            correction = 12;
        }
        return source.y + (target.y - source.y) / (link.isCoachee() ? 1.3 : 1.5) + correction;
    }

    protected nodeClass(node: EmployeeNodeData) {
        if (node.relationshipType === EmployeeRelationshipType.None) {
            return 'user-node';
        }
        let className = 'relation-node';
        if (node.hasEMRelation()) {
            className += ' relation-node--em';
        }
        if (node.hasLTCoachRelation()) {
            className += ' relation-node--lt-coach';
        }
        if (node.hasFirestaerterCoachRelation()) {
            className += ' relation-node--firestaerter';
        }
        return className;
    }

    protected handleNodeClick(node: EmployeeNodeData) {
        if (node.element.Id !== this.selectedElement.Id) {
            this.gui.commandManager.selectUserCommandExecutor.requestExecute({
                userId: node.element._internal_datasource_id_
            });
        }
    }

    protected getCenterNodeDetails() {
        return {
            topText: this.selectedElement.Name,
            bottomText: '',
        };
    }

    protected nodeCX(node: EmployeeNodeData) {
        return node.isCoachee()
            ? Math.max(this.relationCircleR, Math.min(this.svgCanvasWidth / 3 - this.relationCircleR, node.x))
            : node.x;
    }

    protected nodeLabelX(node: EmployeeNodeData, index: number, labels: d3.BaseType[] | d3.ArrayLike<BaseType>) {
        if (node.isCoach()) {
            return node.x + (this.relationCircleR || 0) * 1.2;
        }
        if (node.isCoachee()) {
            return (
                node.x -
                (labels[index] as HTMLElement).getBoundingClientRect().width -
                (this.relationCircleR || 0) * 1.2
            );
        }
        return node.x;
    }
}
